import { Icon_BottomBar24, useBottomSheetDrag } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useParams } from "react-router-dom";

import useNavigation from "../../hooks/navigation";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { useViewLinkId, useWebExport } from "../../hooks/useWebExport";
import { DeepLinkModal } from "../DeepLinkModal/DeepLinkModal";
import { OverlayProvider } from "../Headers/components/ShareModal/components/MenuPortal";
import { Toolbar } from "../Toolbar/Toolbar";
import { ToolButton } from "../ToolButton/ToolButton";
import { ProjectMetadata } from "./ProjectMetadata/ProjectMetadata";
import { ProjectVersionSelector } from "./ProjectVersionSelector/ProjectVersionSelector";

import style from "./ProjectSidebarMobile.module.css";

interface ProjectSidebarMobileProps {
  isOpen: boolean;
  setOpen?: (open: boolean) => void;
  isCommentingEnabled: boolean;
}

export const ProjectSidebarMobile = ({
  isOpen,
  setOpen,
  isCommentingEnabled,
}: ProjectSidebarMobileProps) => {
  const { data: webExportData } = useWebExport();
  const viewLinkId = useViewLinkId();
  const { projectId = "", drawingId = "" } = useParams();

  const navigation = useNavigation();
  const tracker = useTracker();
  const { t, m } = useTranslation();

  const { sheetRef, handleProps } = useBottomSheetDrag({
    onClose: () => setOpen?.(false),
  });

  return (
    <>
      <div className={style.controlButton}>
        <ToolButton
          alt={t(m.ModelViewer.ProjectOutline)}
          title={t(m.ModelViewer.ProjectOutline)}
          icon={<Icon_BottomBar24 />}
          onClick={() => {
            const newState = isOpen ? "close" : "open";
            tracker.trackProjectSidebarToggled({ newState });
            setOpen?.(!isOpen);
          }}
          isActive={isOpen}
          hideShadow
        />
      </div>
      {!drawingId && isCommentingEnabled && (
        <div className={style.toolbarWrapper}>
          <Toolbar />
        </div>
      )}

      <div ref={sheetRef} className={classNames(style.bottomSheet, { [style.isOpen]: isOpen })}>
        <div
          className={style.handle}
          onClick={() => {
            const newState = isOpen ? "close" : "open";
            tracker.trackProjectSidebarToggled({ newState });
            setOpen?.(!isOpen);
          }}
          {...handleProps}
        >
          <div className={style.handleBar} />
        </div>

        <div className={style.content}>
          <ProjectMetadata />

          <OverlayProvider>
            <div className={style.versionsWrapper}>
              <ProjectVersionSelector />
              {webExportData && (
                <div className={style.modelList}>
                  <button
                    className={classNames([style.listItem, !drawingId && style.active])}
                    onClick={() => {
                      navigation.goToViewLink({
                        viewLinkId: viewLinkId ?? "",
                        projectId,
                      });
                      setOpen?.(false);
                    }}
                  >
                    {webExportData.revision?.thumbnailDownloads?.light?.url && (
                      <div className={style.listItemIcon}>
                        <img
                          src={webExportData.revision?.thumbnailDownloads?.light?.url}
                          alt={t(m.ModelViewer.ModelThumbnailAlt)}
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      </div>
                    )}

                    <div>{t(m.ModelViewer.Model)}</div>
                  </button>
                </div>
              )}
              {webExportData?.webExport.drawingsDownload?.length && (
                <div className={style.drawingsList}>
                  <div className={style.drawingsTitle}>{t(m.ModelViewer.Drawings)}</div>

                  {webExportData.webExport.drawingsDownload.map((drawing) => (
                    <button
                      key={drawing.drawingId}
                      className={classNames([
                        style.listItem,
                        drawing.drawingId === drawingId && style.active,
                      ])}
                      onClick={() => {
                        navigation.goToDrawing({
                          viewLinkId: viewLinkId ?? "",
                          projectId,
                          drawingId: drawing.drawingId,
                        });
                        setOpen?.(false);
                      }}
                    >
                      <div>{drawing.title}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </OverlayProvider>
        </div>
      </div>

      <DeepLinkModal />
    </>
  );
};
